import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle } from 'lucide-react';
import { useUserChat } from '../../contexts/UserChatContext';
import { useApi } from '../../contexts/ApiContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate, formatTime } from '../../utils/dateFormatter';
import './UserChatSidebar.css';

interface Message {
  id: number;
  user_id: number;
  username: string;
  content: string;
  tags: string[];
  mentions: string[];
  created_at: string;
}

export const UserChatSidebar: React.FC = () => {
  const { isOpen, setIsOpen, refreshUnreadStatus, unreadCount } = useUserChat();
  const { api } = useApi();
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Cargar mensajes iniciales
  useEffect(() => {
    if (isOpen) {
      loadMessages();
    }
  }, [isOpen]);

  // Scroll al fondo al recibir mensajes
  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  // Polling de mensajes nuevos si está abierto (cada 5s)
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (isOpen) {
      interval = setInterval(() => {
        // Solo cargar si ya tenemos mensajes para buscar los nuevos
        if (messages.length > 0) {
          const lastId = messages[messages.length - 1].id;
          loadNewMessages(lastId);
        } else {
          loadMessages();
        }
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [isOpen, messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async () => {
    try {
      const response = await api.get('/chat/messages?limit=50');
      // La API devuelve del más reciente al más antiguo, invertir para mostrar
      // Nota: El repositorio ya los devuelve en orden cronológico si se pide
      // Revisar implementación repo: "rows.reverse()" -> cronológico (antiguo -> nuevo)
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const loadNewMessages = async (afterId: number) => {
    try {
      const response = await api.get(`/chat/messages?afterId=${afterId}`);
      if (response.data.length > 0) {
        setMessages(prev => [...prev, ...response.data]);
        // Refrescar status global por si acaso
        refreshUnreadStatus();
      }
    } catch (error) {
      console.error('Error loading new messages:', error);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading) return;

    const content = inputMessage; // Guardar contenido
    setInputMessage(''); // Limpiar input optimista
    setLoading(true); // Prevenir doble envío rápido

    try {
      const response = await api.post('/chat/messages', { content });
      setMessages(prev => [...prev, response.data]);
      scrollToBottom();
    } catch (error) {
      console.error('Error sending message:', error);
      setInputMessage(content); // Restaurar si falla
    } finally {
      setLoading(false);
    }
  };



  const isDifferentDay = (current: string, prev: string) => {
    const d1 = new Date(current);
    const d2 = new Date(prev);
    return d1.toDateString() !== d2.toDateString();
  };

  return (
    <>
      {isOpen && (
        <>
          <div className="user-chat-overlay" onClick={() => setIsOpen(false)} />
          <div className="user-chat-sidebar">
        <div className="user-chat-header">
          <h3>Chat de Equipo</h3>
          <button className="close-btn" onClick={() => setIsOpen(false)}>✕</button>
        </div>
        
        <div className="user-chat-messages" ref={chatContainerRef}>
          {messages.length === 0 && (
            <div className="empty-chat">
              <p>No hay mensajes aún.</p>
              <p>¡Inicia la conversación!</p>
            </div>
          )}
          
          {messages.map((msg, index) => {
            const isMe = msg.user_id === user?.id;
            const showDate = index === 0 || isDifferentDay(msg.created_at, messages[index - 1].created_at);
            const isMention = msg.content.includes(`@${user?.username}`);

            return (
              <React.Fragment key={msg.id}>
                {showDate && (
                  <div className="chat-date-separator">
                    <span>{formatDate(msg.created_at)}</span>
                  </div>
                )}
                <div className={`chat-message ${isMe ? 'me' : 'other'} ${isMention ? 'mention' : ''}`}>
                  {!isMe && <div className="message-author">{msg.username}</div>}
                  <div className="message-content">
                    {msg.content} {/* TODO: Parsear links/tags/menciones */}
                  </div>
                  <div className="message-time">
                    {formatTime(msg.created_at)}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        <form className="user-chat-input-area" onSubmit={handleSendMessage}>
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Escribe un mensaje... (@usuario #tag)"
            disabled={loading}
            autoFocus
          />
          <button type="submit" disabled={!inputMessage.trim() || loading}>
            Enviar
          </button>
        </form>
          </div>
        </>
      )}
      <button 
        className="user-chat-floating-btn"
        onClick={() => setIsOpen(!isOpen)}
        title="Chat de Equipo"
      >
        <MessageCircle size={20} />
        {unreadCount > 0 && (
          <span className="user-chat-floating-badge">{unreadCount}</span>
        )}
      </button>
    </>
  );
};

